import { useQueryClient } from "@tanstack/react-query";
import { useCreateEligibilitySelectionForApplication } from "@lending/hooks/queries/EligibilityCriteria/useCreateEligibilitySelectionForApplication";
import { useCreatePersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useCreatePersonalLoanApplicationQuery";
import { useGetPersonalLoanProductsQuery } from "@lending/hooks/queries/PersonalLoanProduct/useGetPersonalLoanProductsQuery";
import { withMlaStatusMetadata } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";
import { PREFILL_DATA_SOURCE } from "@lending/types";
import { useStore } from "../../../../contexts/StoreContext";
import type { PersonalInfoApiData } from "@lending/hooks/queries/PersonalInfo/types";

function useCreatePersonalLoanApplication() {
  const queryClient = useQueryClient();
  const { data: storeData, updateData: updateStoreData } = useStore();
  const { personalLoanProductsData } = useGetPersonalLoanProductsQuery();

  const createPersonalLoanApplication = useCreatePersonalLoanApplicationQuery();
  const { createEligibilitySelectionForApplication } =
    useCreateEligibilitySelectionForApplication();

  async function createApplication(data: PersonalInfoApiData, captcha?: string | null) {
    const selectedProduct = storeData.selectedProduct ?? personalLoanProductsData.results[0].uuid;

    await queryClient.invalidateQueries({ queryKey: ["csrfToken"] });

    const metadata = withMlaStatusMetadata(storeData.applicationMetadata, storeData.mlaStatus);
    const prefillDataSource = storeData.prefillDataSource ?? PREFILL_DATA_SOURCE.MANUAL;

    // The primary applicant is created atomically with the application, so an application is
    // never left without one; a co-applicant is added separately after this step.
    const application = await createPersonalLoanApplication.mutateAsync({
      // @ts-expect-error openapi-typescript does not respect readonly payload fields
      body: {
        product: selectedProduct,
        origin: "institution_site",
        primary_applicant: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          tax_id: data.tax_id,
          date_of_birth: data.date_of_birth,
          prefill_data_source: prefillDataSource,
        },
        ...(captcha ? { captcha } : {}),
        ...(metadata ? { metadata } : {}),
      },
    });

    updateStoreData({ lendingApplicationID: application.id });

    await createEligibilitySelectionForApplication(application.id);

    updateStoreData({
      tax_id: data.tax_id,
      date_of_birth: data.date_of_birth,
      prefillDataSource,
      skippedPlaidPrefill: prefillDataSource === PREFILL_DATA_SOURCE.MANUAL,
    });

    return { application, applicantDetails: application.primary_applicant };
  }

  return { createApplication };
}

export { useCreatePersonalLoanApplication };
